import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { ArrowRight, Loader2, Search } from "lucide-react";
import { gtmPublic, type PublicDiscoverResponse } from "../../lib/gtm";
import { Button, ButtonLink } from "../ui";

/**
 * Home-page search over PUBLISHED data: which live datasets on the network answer a question, the exact query DBIndex
 * would run, and what it costs. Keyless and free; it never returns provider rows (providers are paid per query), so
 * the answer itself is one step away with an API key or a provider-enabled free sample. Unanswered questions become
 * demand signals for data owners, exactly like /discover.
 */
const ago = (s: number) => (s < 90 ? `${Math.max(1, Math.round(s))} sec` : s < 5400 ? `${Math.round(s / 60)} min` : `${Math.round(s / 3600)} h`);
const SUGGESTIONS = ["Products in stock", "Average list price by manufacturer", "Cheapest diesel near Sydney"];

export function PublicSearch() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<PublicDiscoverResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (question: string) => {
    const v = question.trim();
    if (v.length < 3) return;
    setBusy(true); setError(null); setRes(null);
    try { setRes(await gtmPublic.discover({ question: v })); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const submit = (e: FormEvent) => { e.preventDefault(); void run(q); };
  const top = (res?.candidates ?? []).filter((c) => c.eligible).slice(0, 3);
  const full = `/discover?q=${encodeURIComponent(q.trim())}`;
  return (
    <section aria-label="Search published data" className="mt-8 max-w-2xl">
      <form onSubmit={submit} role="search" className="flex gap-2">
        <div className="flex h-12 flex-1 items-center gap-2.5 rounded-full border border-border-strong bg-bg-elev px-4 focus-within:border-accent">
          <Search className="size-4 shrink-0 text-fg-3" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search live data on the network…" aria-label="Search published data" className="min-w-0 flex-1 bg-transparent text-[15px] text-fg placeholder:text-fg-3 focus:outline-none" />
        </div>
        <Button type="submit" variant="primary" size="lg" loading={busy} disabled={q.trim().length < 3} className="rounded-full">Search</Button>
      </form>
      {!res && !busy && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s) => (
            <button key={s} type="button" onClick={() => { setQ(s); void run(s); }} className="rounded-full border border-border px-3 py-1 text-xs text-fg-2 hover:border-border-strong hover:text-fg">{s}</button>
          ))}
        </div>
      )}
      {busy && <p className="mt-4 flex items-center gap-2 text-sm text-fg-3"><Loader2 className="size-4 animate-spin" aria-hidden /> Searching the network…</p>}
      {error && <p role="alert" className="mt-4 text-sm text-fg-2">{error}</p>}
      {res && (
        <div className="mt-4 rounded-xl border border-border bg-panel">
          {top.length ? (
            <ul className="divide-y divide-border">
              {top.map((c) => (
                <li key={c.datasetId} className="flex flex-col gap-1.5 p-4">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <Link to={c.slug ? `/marketplace/${c.slug}` : full} className="font-medium text-fg hover:text-accent">{c.name}</Link>
                    <span className="text-xs text-fg-3">{c.provider} · {c.categoryLabel}</span>
                    {c.recommended && <span className="rounded-full border border-accent/40 px-2 py-0.5 text-[11px] text-accent">Best match</span>}
                  </div>
                  {c.plan ? <p className="font-mono text-[11px] text-fg-3">Would run: {c.plan.summary}</p> : <p className="text-xs text-fg-3">{c.description}</p>}
                  <p className="text-xs text-fg-2">${c.pricePerQuery} per query{c.actualLagSeconds != null ? ` · ${ago(c.actualLagSeconds)} fresh` : ""}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="p-4 text-sm text-fg-2">{res.demand?.message ?? "No live dataset answers that yet."} We've recorded the request so data owners can see the demand.</p>
          )}
          <div className="flex flex-wrap items-center gap-2 border-t border-border p-3">
            <ButtonLink to={full} variant="ghost" size="sm">All results <ArrowRight className="size-3.5" aria-hidden /></ButtonLink>
            {top.length > 0 && <ButtonLink to="/signup" variant="primary" size="sm" className="ml-auto">Get the answer with free credits</ButtonLink>}
          </div>
        </div>
      )}
    </section>
  );
}
